// Phone number helpers (US / NANP only).
//
// A US number is 10 national digits: area code (3) + exchange (3) + line (4).
// It MAY be prefixed with the "1" country code. NANP area codes never start
// with 0 or 1, so a leading "1" is ALWAYS the country code (never part of the
// real number) and a national number whose first digit is 0 or 1 is invalid.

/**
 * Normalize to the 10 national digits for storage / the API.
 *
 * Strips every non-digit, drops a leading "1" country code, caps at 10.
 * Use at the save boundary (after isValidUsPhone has passed).
 *
 *   cleanUsPhone('1 (415) 656-5645') -> '4156565645'
 *   cleanUsPhone('(415) 656-5645')   -> '4156565645'
 *   cleanUsPhone('+1 415 656 5645')  -> '4156565645'
 */
export function cleanUsPhone(value) {
    let digits = String(value == null ? '' : value).replace(/\D/g, '');
    if (digits.charAt(0) === '1') {
        digits = digits.slice(1);
    }
    return digits.slice(0, 10);
}

/**
 * Input normalizer for keystroke/paste handlers.
 *
 * Keeps an optional leading "1" country code (so it stays VISIBLE while typing,
 * e.g. "1 (555) 555-5555") and caps the national part at 10 digits — at most 11
 * digits total. Store this raw value in the model; format it for display with
 * formatUsPhone() and normalize it for the API with cleanUsPhone().
 *
 *   cleanUsPhoneInput('1')            -> '1'
 *   cleanUsPhoneInput('15555555555')  -> '15555555555'  (1 + 10 national)
 *   cleanUsPhoneInput('5555555555')   -> '5555555555'
 *   cleanUsPhoneInput('115555555555') -> '11555555555'  (kept; invalid area code, caught on validate)
 */
export function cleanUsPhoneInput(value) {
    const digits = String(value == null ? '' : value).replace(/\D/g, '');
    if (digits.charAt(0) === '1') {
        return '1' + digits.slice(1, 11);
    }
    return digits.slice(0, 10);
}

/**
 * Format for display. Shows the "1 " country code when present, then the
 * national digits as "(555) 555-5555". Formats progressively while typing.
 *
 *   formatUsPhone('1')           -> '1'
 *   formatUsPhone('15')          -> '1 (5'
 *   formatUsPhone('15555555555') -> '1 (555) 555-5555'
 *   formatUsPhone('5555555555')  -> '(555) 555-5555'
 *   formatUsPhone('')            -> ''
 */
export function formatUsPhone(value) {
    let digits = String(value == null ? '' : value).replace(/\D/g, '');
    let countryCode = '';
    if (digits.charAt(0) === '1') {
        countryCode = '1';
        digits = digits.slice(1);
    }
    digits = digits.slice(0, 10);

    let formatted = '';
    if (digits.length > 0) {
        formatted = '(' + digits.substring(0, 3);
        if (digits.length >= 4) {
            formatted += ') ' + digits.substring(3, 6);
        }
        if (digits.length >= 7) {
            formatted += '-' + digits.substring(6, 10);
        }
    }

    if (countryCode) {
        return formatted ? '1 ' + formatted : '1';
    }
    return formatted;
}

/**
 * True only for a valid 10-digit US national number, ignoring an optional
 * leading "1" country code. The national number must be 10 digits with an
 * area code that starts 2-9 (NANP). This rejects:
 *   - too few / too many digits
 *   - a national number whose area code starts with 0 or 1, e.g. the
 *     "two leading ones" typo 1 (155) 555-5555  ->  national 1555555555
 *
 *   isValidUsPhone('1 (555) 555-5555') -> true
 *   isValidUsPhone('(555) 555-5555')   -> true
 *   isValidUsPhone('1 (155) 555-5555') -> false  (area code starts with 1)
 *   isValidUsPhone('555) 555-555')     -> false  (only 9 digits)
 */
export function isValidUsPhone(value) {
    let digits = String(value == null ? '' : value).replace(/\D/g, '');
    if (digits.charAt(0) === '1') {
        digits = digits.slice(1);
    }
    return /^[2-9]\d{9}$/.test(digits);
}
